import React, { useEffect } from "react";
import { useForm } from "@mantine/form";
import { Text, MultiSelect, Loader } from "@mantine/core";
import InputField from "src/components/common/Inputs/InputField";
import { CustomButton } from "../../common/Buttons/CustomButton";
import SelectField from "../../common/Inputs/SelectField";
import { getSchoolsByLevel } from "./metadata";
import {
  ChildUpdationData,
  ChildrenData,
  Subject,
} from "src/interfaces/Children";
import { ProfileCard } from "src/components/ProfileCard";
import useChildrenData from "src/hooks/useChildrenData";

interface EditChildProps {
  user: ChildrenData;
  onUpdate: (updatedData: Partial<ChildUpdationData>) => void;
}

export const EditChildForm: React.FC<EditChildProps> = ({ user, onUpdate }) => {
  const {
    gradeLevels,
    subjects,
    loading: isLoadingSubjects,
    fetchSubjectsByGrade,
    updateChild,
  } = useChildrenData();

  // Find the current level ID from grade levels
  const currentLevel = gradeLevels.find((level) => level.name === user.level);
  const levelId = currentLevel?.id;

  // Fetch subjects when component mounts or level changes
  useEffect(() => {
    if (levelId) {
      fetchSubjectsByGrade(levelId).then(() => {
        const requiredSubjects = subjects
          .filter((subject) => subject.is_required)
          .map((subject) => subject.id);

        const userSubjectIds = (user.subjects || []).map((s) =>
          typeof s === "string" ? s : s.id
        );

        const initialSubjects = Array.from(
          new Set([...userSubjectIds, ...requiredSubjects])
        );

        form.setFieldValue("subjects", initialSubjects);
      });
    }
  }, []);

  const form = useForm<ChildUpdationData>({
    initialValues: {
      name: user.name,
      level: user.level as string,
      school: user.child_school,
      subjects: user.subjects ? (user.subjects as string[]) : [],
      password: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      // Password validation
      if (
        values.password &&
        !/^(?=.*[A-Z])(?=.*\d).{8,}$/.test(values.password)
      ) {
        errors.password =
          "Password must be at least 8 characters long, contain at least one uppercase letter and one number";
      }

      // At least one subject must be selected
      if (!values.subjects || values.subjects.length === 0) {
        errors.subjects = "At least one subject must be selected";
      }

      // Check if all required subjects are selected
      const requiredSubjects = subjects
        .filter((subject) => subject.is_required)
        .map((subject) => subject.id);

      const missingRequiredSubjects = requiredSubjects.filter(
        (subjectId) => !values.subjects?.includes(subjectId)
      );

      if (missingRequiredSubjects.length > 0) {
        errors.subjects = `The following required subjects must be selected: ${missingRequiredSubjects
          .map((id) => subjects.find((s) => s.id === id)?.name || id)
          .join(", ")}`;
      }

      return errors;
    },
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    const updatedData: Partial<ChildUpdationData> = {};

    // Only include fields that have changed
    if (values.name !== user.name) updatedData.name = values.name;
    if (values.school !== user.child_school) updatedData.school = values.school;
    if (values.password) updatedData.password = values.password;
    if (values.subjects) updatedData.subjects = values.subjects;

    try {
      await updateChild(user.username, updatedData);
      onUpdate(updatedData);
    } catch (error) {
      console.error("Error updating child:", error);
    }
  };

  return (
    <div className='w-full p-4'>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <div className='space-y-6'>
          <div className='flex items-center mb-6'>
            <ProfileCard
              avatar={user.profile_picture_url}
              name={user.name}
              username={user.username}
              subjects={
                Array.isArray(user.subjects)
                  ? user.subjects.map((subject) =>
                      typeof subject === "object" ? subject.name : subject
                    )
                  : []
              }
            />
          </div>
          <div className='space-y-4'>
            <InputField
              label='Name'
              placeholder="Enter child's name"
              {...form.getInputProps("name")}
              required
            />

            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>
                Level
                {!currentLevel && <Loader size='xs' className='ml-2 inline' />}
              </label>
              <div className='px-3 py-2 bg-gray-50 rounded-md border border-gray-200 text-gray-900'>
                {currentLevel ? currentLevel.name : "Loading..."}
              </div>
              <input type='hidden' {...form.getInputProps("level")} />
            </div>

            <SelectField
              label='School'
              placeholder='Select school'
              data={getSchoolsByLevel(form.values.level)}
              searchable
              {...form.getInputProps("school")}
              required
            />

            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>
                Subjects
                {isLoadingSubjects && (
                  <Loader size='xs' className='ml-2 inline' />
                )}
              </label>
              <div>
                <MultiSelect
                  data={subjects.map((subject) => ({
                    value: subject.id,
                    label: subject.name,
                    disabled: subject.is_required, // Disable required subjects so they can't be deselected
                  }))}
                  placeholder={
                    isLoadingSubjects
                      ? "Loading subjects..."
                      : "Select subjects"
                  }
                  searchable
                  nothingFound='No subjects found'
                  {...form.getInputProps("subjects")}
                  disabled={isLoadingSubjects || !levelId}
                  className='w-full'
                  error={form.errors.subjects}
                  value={form.values.subjects || []}
                  onChange={(selected) => {
                    // Always include required subjects in the selection
                    const requiredSubjects = subjects
                      .filter((subject) => subject.is_required)
                      .map((subject) => subject.id);

                    // Combine required subjects with user's selection
                    const newSelection = Array.from(
                      new Set([...requiredSubjects, ...selected])
                    );

                    form.setFieldValue("subjects", newSelection);
                  }}
                />
                {/* {form.errors.subjects && (
                  <div className='text-red-500 text-xs mt-1'>
                    {form.errors.subjects}
                  </div>
                )} */}
              </div>
            </div>

            <InputField label='Username' value={user.username} disabled />

            <InputField
              label='New Password'
              type='password'
              placeholder='Enter new password (leave blank to keep current)'
              {...form.getInputProps("password")}
              showPasswordToggle
            />

            <Text size='xs' color='gray' mt={-8} mb={4}>
              Use at least one capital letter and a number in your password.
            </Text>

            <div className='pt-2'>
              <CustomButton type='submit' disabled={!form.isValid()}>
                Update Information
              </CustomButton>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
